import type React from 'react'

type Compact = 'xs' | 'sm' | 'md' | undefined

/**
 * A compact schedule block whose name has room for one line only. Its heart
 * sits beside the name instead of floating over it, so the name can
 * `truncate` at the last character that fits — a one-line clamp cuts at the
 * last whole word.
 */
export function isOneLineName(compact: Compact, nameLines: number | undefined): boolean {
  return compact === 'xs' || ((compact === 'sm' || compact === 'md') && (nameLines ?? 1) <= 1)
}

/**
 * The name line's type classes, shared by a card's plain name and the b2b
 * name. Short blocks cap the size (sm at text-xs, md at text-sm) so the lines
 * `nameLines` counts on are the lines it gets.
 */
export function cardNameClass(compact: Compact, nameLines: number | undefined): string {
  if (compact === 'xs') return 'text-[10px] truncate flex-1 min-w-0'
  const size = cardNameSize(compact)
  if (isOneLineName(compact, nameLines)) return `${size} truncate flex-1 min-w-0 select-none`
  return compact ? `${size} line-clamp-1 break-words select-none` : `${size} select-none`
}

/** The name's type size alone — also set on the floated heart, so `1lh` there is one name line. */
export function cardNameSize(compact: Compact): string {
  return compact === 'sm' || compact === 'xs'
    ? 'text-[10px] @[88px]:text-xs'
    : compact === 'md'
      ? 'text-[10px] @[88px]:text-xs @[128px]:text-sm'
      : 'text-[10px] @[88px]:text-xs @[128px]:text-sm @[168px]:text-base @[208px]:text-lg'
}

/** Widens `cardNameClass`'s one-line clamp to the lines a compact block has room for. */
export function cardNameStyle(compact: Compact, nameLines: number | undefined): React.CSSProperties | undefined {
  return compact && !isOneLineName(compact, nameLines) && nameLines ? { WebkitLineClamp: nameLines } : undefined
}
